import SafeIcon from '../common/SafeIcon';
import { virtualReceptionistAdvantages, virtualComparisonRows } from '../data/virtualReceptionistPageContent';

export const HumanServiceSection = () => (
  <section className="border-y border-slate-800 bg-slate-900/50 px-4 py-24 sm:px-6 lg:px-8">
    <div className="mx-auto max-w-6xl">
      <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
        <div>
          <p className="mb-4 text-sm font-bold uppercase tracking-widest text-amber-400">The modern dilemma</p>
          <h2 className="text-4xl font-extrabold text-white">Human answering services can hide expensive friction.</h2>
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          {[
            ['Per-minute bill shock', '$1.75–$3.50 per minute can make long calls and robocalls surprisingly expensive.'],
            ['Transfer delays', 'Callers may wait while an operator reaches your team, increasing the chance of a dropped lead.'],
            ['Shallow knowledge', 'Static scripts often cannot answer detailed questions about pricing, services, or policies.'],
            ['Limited off-hours coverage', 'Overnight, weekend, and holiday coverage may require premium tiers or extra fees.']
          ].map(([title, description]) => (
            <article key={title} className="rounded-2xl border border-slate-800 bg-slate-950/70 p-6">
              <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/10">
                <SafeIcon name="AlertTriangle" className="text-amber-400" />
              </div>
              <h3 className="mb-2 text-lg font-bold text-white">{title}</h3>
              <p className="text-sm leading-relaxed text-slate-400">{description}</p>
            </article>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export const AiAdvantageSection = () => (
  <section className="px-4 py-24 sm:px-6 lg:px-8">
    <div className="mx-auto max-w-6xl">
      <div className="mb-14 max-w-3xl">
        <p className="mb-4 text-sm font-bold uppercase tracking-widest text-blue-400">The AI virtual receptionist advantage</p>
        <h2 className="text-4xl font-extrabold text-white">More coverage, faster answers, and fewer billing surprises.</h2>
      </div>
      <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {virtualReceptionistAdvantages.map((advantage) => (
          <article key={advantage.title} className="rounded-3xl border border-slate-800 bg-slate-900/70 p-7 transition hover:-translate-y-1 hover:border-blue-500/50">
            <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-500/10">
              <SafeIcon name={advantage.icon} className="text-xl text-blue-400" />
            </div>
            <h3 className="mb-3 text-xl font-bold text-white">{advantage.title}</h3>
            <p className="leading-relaxed text-slate-400">{advantage.description}</p>
          </article>
        ))}
      </div>
    </div>
  </section>
);

export const VirtualComparisonSection = () => (
  <section className="bg-slate-900/50 px-4 py-24 sm:px-6 lg:px-8">
    <div className="mx-auto max-w-6xl">
      <div className="mb-12 text-center">
        <p className="mb-4 text-sm font-bold uppercase tracking-widest text-blue-400">Head-to-head</p>
        <h2 className="text-4xl font-extrabold text-white">Traditional virtual receptionist vs. AutoReception AI</h2>
      </div>
      <div className="overflow-x-auto rounded-3xl border border-slate-800">
        <table className="min-w-[760px] w-full text-left">
          <thead className="bg-slate-800 text-sm uppercase tracking-wider text-slate-300">
            <tr>
              <th className="p-5">Metric / Capability</th>
              <th className="p-5">Traditional Virtual Receptionist</th>
              <th className="p-5 text-blue-300">AutoReception AI</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800 bg-slate-950/80">
            {virtualComparisonRows.map((row) => (
              <tr key={row[0]} className="text-sm text-slate-300">
                <th className="p-5 font-semibold text-white">{row[0]}</th>
                <td className="p-5">{row[1]}</td>
                <td className="p-5 font-semibold text-emerald-400">{row[2]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </section>
);

export const SwitchingSection = () => (
  <section className="px-4 py-24 sm:px-6 lg:px-8">
    <div className="mx-auto max-w-6xl">
      <div className="mb-14 text-center">
        <p className="mb-4 text-sm font-bold uppercase tracking-widest text-emerald-400">Make the switch</p>
        <h2 className="text-4xl font-extrabold text-white">Go live in three practical steps.</h2>
      </div>
      <div className="grid gap-6 md:grid-cols-3">
        {[
          ['01', 'Connect your schedule', 'Sync your booking software and provide your business FAQ, services, policies, and escalation rules.'],
          ['02', 'Forward your line', 'Use conditional forwarding for missed calls and after-hours coverage, or route every inbound call.'],
          ['03', 'Let AI take the front desk', 'Review summaries, listen to recordings, monitor lead quality, and watch appointments fill your calendar.']
        ].map(([number, title, description]) => (
          <article key={number} className="rounded-3xl border border-slate-800 bg-slate-900 p-7">
            <span className="text-5xl font-black text-blue-500/40">{number}</span>
            <h3 className="mt-5 text-xl font-bold text-white">{title}</h3>
            <p className="mt-3 leading-relaxed text-slate-400">{description}</p>
          </article>
        ))}
      </div>
    </div>
  </section>
);

export const SavingsSection = () => (
  <section id="savings" className="border-y border-slate-800 bg-slate-950 px-4 py-24 sm:px-6 lg:px-8">
    <div className="mx-auto max-w-5xl">
      <div className="mb-12 text-center">
        <p className="mb-4 text-sm font-bold uppercase tracking-widest text-blue-400">Real-world economics</p>
        <h2 className="text-4xl font-extrabold text-white">What 150 calls per month can cost</h2>
      </div>
      <div className="grid gap-6 md:grid-cols-2">
        <div className="rounded-3xl border border-amber-500/20 bg-amber-500/5 p-8">
          <p className="text-sm font-bold uppercase tracking-wider text-amber-300">Traditional live receptionist</p>
          <p className="mt-4 text-5xl font-extrabold text-white">$450–$750</p>
          <p className="mt-2 text-slate-400">Estimated monthly cost for approximately 150 calls before premium after-hours coverage and additional-minute charges.</p>
        </div>
        <div className="rounded-3xl border border-emerald-500/30 bg-emerald-500/10 p-8">
          <p className="text-sm font-bold uppercase tracking-wider text-emerald-300">AutoReception AI</p>
          <p className="mt-4 text-5xl font-extrabold text-white">$699</p>
          <p className="mt-2 text-slate-300">A predictable monthly plan with always-on answering, concurrent call handling, automated booking, and lead follow-up workflows.</p>
        </div>
      </div>
      <p className="mx-auto mt-8 max-w-3xl text-center text-slate-400">
        The right comparison is not only the monthly invoice. Include missed after-hours revenue, booking automation, transfer charges, setup fees, and the cost of calls your current provider cannot answer.
      </p>
    </div>
  </section>
);